import type { Dataset } from './dataset.js';

/**
 * Keeping each record's change history on the device (ADR-0025).
 *
 * History is append-only and immutable, which changes what "syncing" it
 * means: a copy already held is never wrong, only ever incomplete. So there
 * is no watermark and no merge — there is a set of records whose history is
 * missing or has been invalidated, and they are fetched in chunks.
 *
 * Staleness comes from the row replica rather than from a version check. A
 * tag added to a transaction changes its history and does not bump
 * `transaction.version`, because the link is its own record; a version check
 * would quietly miss exactly the case the history panel was extended to
 * cover. `Dataset.historyOwnerOf` maps a changed row to the record whose
 * history it belongs to, and that marks it.
 */

export interface HistorySyncOptions {
  dataset: Dataset;
  /** Which records could have a history — for webledger, every live transaction. */
  candidates: () => readonly string[];
  /** One request per chunk. The server caps the id list; this stays under it. */
  fetchHistories: (ids: readonly string[]) => Promise<Record<string, unknown>>;
  chunkSize?: number;
  /** How many chunks one pass will fetch. The rest wait for the next pass. */
  maxChunksPerPass?: number;
}

/** The server's own cap is 200; matching it keeps a chunk to one request. */
const DEFAULT_CHUNK = 200;
/**
 * A cold device holds nothing, and fetching a thousand transactions' history
 * in one go would compete with the reads the person is actually waiting for.
 * Three chunks a pass fills a typical ledger within a few syncs and never
 * monopolises the connection.
 */
const DEFAULT_MAX_CHUNKS = 3;

export class HistorySync {
  private running = false;

  constructor(private readonly opts: HistorySyncOptions) {}

  /**
   * Fetch what is missing, a few chunks at a time.
   *
   * Never runs twice at once: two passes would ask for the same ids, because
   * neither has stored anything yet when the other builds its list.
   */
  async fill(): Promise<number> {
    if (this.running) return 0;
    this.running = true;
    try {
      const chunk = this.opts.chunkSize ?? DEFAULT_CHUNK;
      const maxChunks = this.opts.maxChunksPerPass ?? DEFAULT_MAX_CHUNKS;
      const wanted = this.opts.dataset.missingHistories([...this.opts.candidates()]);
      let stored = 0;
      for (let i = 0; i < wanted.length && i < chunk * maxChunks; i += chunk) {
        const ids = wanted.slice(i, i + chunk);
        const histories = await this.opts.fetchHistories(ids);
        for (const [id, payload] of Object.entries(histories)) {
          await this.opts.dataset.putHistory(id, payload);
          stored += 1;
        }
        /*
         * An id the server did not answer for is stored as an empty history
         * rather than left outstanding.
         *
         * Without this, a transaction with no history yet — or one the server
         * declines to describe — would be re-requested on every single pass,
         * for ever, and would keep the queue permanently non-empty.
         */
        for (const id of ids) {
          if (!(id in histories)) {
            await this.opts.dataset.putHistory(id, { commits: [], revisions: [], splits: [] });
          }
        }
      }
      return stored;
    } finally {
      this.running = false;
    }
  }
}
